import {
  Button,
  ButtonColor,
  ButtonState,
} from '@delmonicos/shared-design-system-react/src/components/button/button';
import {
  Icon,
  IconColor,
  IconName,
} from '@delmonicos/shared-design-system-react/src/components/icon/icon';
import { Spinner } from '@delmonicos/shared-design-system-react/src/components/spinner/spinner';
import {
  GapEnum,
  Stack,
} from '@delmonicos/shared-design-system-react/src/components/stack/stack';
import { Tag } from '@delmonicos/shared-design-system-react/src/components/tag/tag';
import {
  Text,
  TextColor,
} from '@delmonicos/shared-design-system-react/src/components/typography/text/text';
import { GatewayTicketModel } from '@delmonicos/shared-sdk/libs/cpms/src/apps/charge-point-operators-gateway/src/tickets/models/outputs/gateway-ticket.model';
import { formatDate } from '@delmonicos/shared-sdk/libs/cpms/src/clients/utils/formatters/format-date';
import { TicketStatus } from '@delmonicos/shared-sdk/libs/cpms/src/libs/core/src/enums/ticket-status.enum';
import classNames from 'classnames';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import i18n from '@app/core/locale/i18n';
import { useCpmsBoGetTicketsPaginatedQuery } from '@service/cpms-bo-ticket.service';

import { PriorityTagComponent } from '../shared/priority-tag/priority-tag.component';
import * as css from './ticket-list.module.scss';

const PAGE_SIZE = 10;

const STATUS_CONFIG: Record<
  TicketStatus,
  {
    tagClass: string;
    icon: IconName;
    iconColor: IconColor;
    textColor: TextColor;
  }
> = {
  [TicketStatus.New]: {
    tagClass: 'bg-surface-brand',
    textColor: TextColor.Brand,
    icon: 'file-new',
    iconColor: IconColor.Brand,
  },
  [TicketStatus.InProgress]: {
    tagClass: 'bg-surface-informations',
    textColor: TextColor.Informations,
    icon: 'file-05',
    iconColor: IconColor.Informations,
  },
  [TicketStatus.Closed]: {
    tagClass: 'bg-surface-success',
    textColor: TextColor.Success,
    icon: 'file-check-02',
    iconColor: IconColor.Success,
  },
};

interface TicketSectionProps {
  status: TicketStatus;
  onActionRead: (id: string) => void;
}

export const TicketSection = ({
  status,
  onActionRead,
}: TicketSectionProps): React.JSX.Element => {
  const { t } = useTranslation();
  const config = STATUS_CONFIG[status];

  const [offset, setOffset] = useState(0);
  const [accumulatedDocs, setAccumulatedDocs] = useState<GatewayTicketModel[]>(
    [],
  );

  const { data, isLoading, isFetching, isError } =
    useCpmsBoGetTicketsPaginatedQuery({
      limit: PAGE_SIZE,
      offset,
      status,
    });

  useEffect(() => {
    if (data?.docs) {
      setAccumulatedDocs((prev) => [
        ...prev.slice(0, data.offset),
        ...data.docs,
      ]);
    }
  }, [data]);

  const hasMore = data?.hasNextPage ?? false;

  return (
    <Stack gap={GapEnum.M} direction="column" flex={1}>
      <Stack direction="row" alignItems="center" gap={GapEnum.S}>
        <Tag
          className={classNames(config.tagClass, css.ticketSectionStatusTag)}
          gap={GapEnum.XXS}>
          <Icon name={config.icon} color={config.iconColor} />
          <Text color={config.textColor}>
            {t(`delmonicos-shared-sdk.enums-ticket-status.${status}`)}
          </Text>
        </Tag>
      </Stack>

      {isLoading && <Spinner />}
      {isError && <p>{t('common.error')}</p>}

      {!isLoading && !isError && accumulatedDocs.length === 0 && (
        <p className={css.ticketSectionEmpty}>{t('$.ticket-list.empty')}</p>
      )}

      {!isLoading && !isError && accumulatedDocs.length > 0 && (
        <Stack direction="column" gap={GapEnum.M}>
          <div className={css.ticketGrid}>
            {accumulatedDocs.map((ticket) => (
              <button
                key={ticket.id}
                className={css.ticketCard}
                onClick={() => onActionRead(ticket.id)}>
                <Stack gap={GapEnum.XS} justifyContent="space-between">
                  <p className={css.ticketCardTitle}>{ticket.title}</p>
                  <PriorityTagComponent priority={ticket.priority} />
                </Stack>
                <p className={css.ticketCardMeta}>
                  {formatDate(ticket.createdAt, i18n)}
                </p>
                {ticket.assignee ? (
                  <p className={css.ticketCardAssignee}>
                    {ticket.assignee.firstName} {ticket.assignee.lastName}
                  </p>
                ) : (
                  <p className={css.ticketCardAssigneeEmpty}>
                    {t('$.ticket-list.no-assignee')}
                  </p>
                )}
              </button>
            ))}
          </div>
          {hasMore && (
            <div className={css.ticketLoadMore}>
              <Button
                color={ButtonColor.Ghost}
                state={isFetching ? ButtonState.Loading : ButtonState.Default}
                isLoadingMessage={t('$.ticket-list.load-more')}
                onClick={() => setOffset((prev) => prev + PAGE_SIZE)}>
                {t('$.ticket-list.load-more')}
              </Button>
            </div>
          )}
        </Stack>
      )}
    </Stack>
  );
};
